/**
 * Weiteren Erziehungsberechtigten an dieses Kind hängen.
 * Eine schon vorhandene Person wird ausgewählt, nicht noch einmal angelegt.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const LEER = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  address: '',
  postal_code: '',
  city: '',
};

export default function CustodianAssignDialog({
  open,
  onOpenChange,
  studentId,
  payerId,
  onChanged,
}) {
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState([]);
  const [formular, setFormular] = useState(LEER);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    if (!open) {
      setSuche('');
      setTreffer([]);
      setFormular(LEER);
      return undefined;
    }
    const begriff = suche.trim();
    if (begriff.length < 2) {
      setTreffer([]);
      return undefined;
    }
    let abgebrochen = false;
    api.get('/guardians', { params: { search: begriff, limit: 8 } })
      .then((res) => {
        if (abgebrochen) return;
        const zeilen = Array.isArray(res.data) ? res.data : [];
        setTreffer(zeilen.filter((person) => person.id !== payerId));
      })
      .catch(() => {
        if (!abgebrochen) setTreffer([]);
      });
    return () => {
      abgebrochen = true;
    };
  }, [open, suche, payerId]);

  const verknuepfen = async (personId) => {
    setLaeuft(true);
    try {
      await api.post(
        `/students/${studentId}/guardians/${personId}`,
        null,
        { params: { as_payer: false, relationship_type: 'custodian' } },
      );
      toast.success('Erziehungsberechtigte:r verknüpft');
      onOpenChange(false);
      await onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verknüpfen fehlgeschlagen');
    } finally {
      setLaeuft(false);
    }
  };

  const anlegen = async () => {
    if (!formular.first_name.trim() || !formular.last_name.trim()) {
      toast.error('Vor- und Nachname sind Pflicht');
      return;
    }
    setLaeuft(true);
    try {
      await api.post(`/students/${studentId}/guardians`, {
        ...formular,
        relationship_type: 'custodian',
      });
      toast.success('Erziehungsberechtigte:r angelegt');
      onOpenChange(false);
      await onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally {
      setLaeuft(false);
    }
  };

  const setFeld = (feld) => (event) => {
    setFormular((prev) => ({ ...prev, [feld]: event.target.value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="custodian-assign-dialog">
        <DialogHeader>
          <DialogTitle>Erziehungsberechtigte:r</DialogTitle>
        </DialogHeader>
        <label className="block text-xs text-slate-500">
          Vorhandene Person suchen
          <input
            className="mt-1 w-full h-8 px-2 text-sm border border-slate-300 rounded"
            value={suche}
            onChange={(event) => setSuche(event.target.value)}
            data-testid="custodian-search"
          />
        </label>
        <ul className="max-h-40 overflow-y-auto">
          {treffer.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                className="w-full text-left px-2 py-1.5 text-sm hover:bg-slate-50"
                disabled={laeuft}
                onClick={() => verknuepfen(person.id)}
                data-testid={`custodian-pick-${person.id}`}
              >
                {`${person.last_name || ''}, ${person.first_name || ''}`.replace(/^, |, $/g, '')}
              </button>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-2 gap-2">
          <input className="h-8 px-2 text-sm border border-slate-300 rounded" placeholder="Vorname" value={formular.first_name} onChange={setFeld('first_name')} data-testid="custodian-first-name" />
          <input className="h-8 px-2 text-sm border border-slate-300 rounded" placeholder="Nachname" value={formular.last_name} onChange={setFeld('last_name')} data-testid="custodian-last-name" />
          <input className="h-8 px-2 text-sm border border-slate-300 rounded" placeholder="Telefon" value={formular.phone} onChange={setFeld('phone')} data-testid="custodian-phone" />
          <input className="h-8 px-2 text-sm border border-slate-300 rounded" placeholder="E-Mail" value={formular.email} onChange={setFeld('email')} data-testid="custodian-email" />
          <input className="col-span-2 h-8 px-2 text-sm border border-slate-300 rounded" placeholder="Adresse" value={formular.address} onChange={setFeld('address')} />
          <input className="h-8 px-2 text-sm border border-slate-300 rounded" placeholder="PLZ" value={formular.postal_code} onChange={setFeld('postal_code')} />
          <input className="h-8 px-2 text-sm border border-slate-300 rounded" placeholder="Ort" value={formular.city} onChange={setFeld('city')} />
        </div>
        <Button type="button" size="sm" onClick={anlegen} disabled={laeuft} data-testid="custodian-create">
          Neu anlegen und verknüpfen
        </Button>
      </DialogContent>
    </Dialog>
  );
}
